import {useI18n} from '@/i18n'
import {Input} from '@/components/ui/input'
import {MutedText} from '@/components/shared/MutedText'
import {transformParamFields, transformDescription, validateTransformArgs, type TransformParamField} from '@/components/shared/transform-args'

interface TransformParamsFieldsProps {
    transform: string | null
    args: Record<string, unknown> | null
    onChange: (args: Record<string, unknown> | null) => void
    disabled?: boolean
    testIdPrefix: string
}

function displayValue(field: TransformParamField, args: Record<string, unknown> | null): string {
    const raw = args?.[field.key]
    if (raw == null) return ''
    if (field.kind === 'list' && Array.isArray(raw)) return raw.join(', ')
    return String(raw)
}

function nextArgs(
    field: TransformParamField,
    args: Record<string, unknown> | null,
    inputValue: string,
): Record<string, unknown> {
    const base = {...(args ?? {})}
    if (field.kind === 'list') {
        const parts = inputValue.split(',').map((s) => s.trim()).filter((s) => s.length > 0)
        base[field.key] = parts
    } else {
        base[field.key] = inputValue
    }
    return base
}

/**
 * Renders the dynamic parameter inputs for the selected attribute-mapping transform.
 * Returns null for transforms that take no parameters.
 */
export function TransformParamsFields({transform, args, onChange, disabled, testIdPrefix}: TransformParamsFieldsProps) {
    const i18n = useI18n()
    const fields = transformParamFields(transform, i18n)
    const description = transformDescription(transform, i18n)
    if (fields.length === 0 && !description) {
        return null
    }
    const error = validateTransformArgs(transform, args, i18n)

    return (
        <div className="mt-2 space-y-2 rounded-md border border-dashed p-2" data-testid={`${testIdPrefix}-params`}>
            {fields.length > 0 && (
                <MutedText size="xs" weight="medium" block>{i18n.transformParams.title}</MutedText>
            )}
            {description && (
                <MutedText size="xs" block data-testid={`${testIdPrefix}-param-description`}>
                    {description}
                </MutedText>
            )}
            {fields.map((field) => (
                <div key={field.key}>
                    <label className="text-xs block mb-1 text-muted-foreground">
                        {field.label}{field.required ? ' *' : ''}
                    </label>
                    <Input
                        value={displayValue(field, args)}
                        onChange={(e) => onChange(nextArgs(field, args, e.target.value))}
                        placeholder={field.placeholder}
                        disabled={disabled}
                        data-testid={`${testIdPrefix}-param-${field.key}`}
                        className="h-8"
                    />
                </div>
            ))}
            {error && (
                <MutedText size="xs" className="text-destructive" data-testid={`${testIdPrefix}-param-error`}>
                    {error}
                </MutedText>
            )}
        </div>
    )
}
